// React Dependencies
import React, { useState, useEffect, useReducer, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Typography, IconButton, TextField, Button, Dialog, Divider } from '@material-ui/core';
import { Edit as EditIcon, Lock as LockIcon } from '@material-ui/icons';

// Internal Dependencies
import ApiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';
import { BinaryOneMBSize, Status, IMAGE_FORMATS } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import FixedCropper from '../../global/image/FixedCropper';
import StampDialog from './StampDialog';
import { isHexColour } from '../../../common/util';
import FloatingColourPicker from '../../FloatingColourPicker';
import OverLoading from '../../global/OverLoading';

const defaultImageObject = { file: null, data: null };

const defaultCropperState = {
  cropImage:     defaultImageObject,
  uploadContext: null,
  isCropping:    false,
};

const defaultLoyaltyStampState = {
  // will be set to false if loyalty card status is either Live or Deactivated
  status:            Status.Deactivated,
  stampCount:        0,
  stamps:            [],
  defaultStampImage: defaultImageObject,
  stampsHolderImage: defaultImageObject,
  redeemImage:       defaultImageObject,
  stampTextColour:   Colours.Black.slice(1),
  dialog:            {
    id:                      null,
    num:                     null,
    stampImage:              defaultImageObject,
    name:                    '',
    label:                   '',
    linkPageId:              null,
    linkPageName:            '',
    isToBeIssueByLinkPageId: false,
    prize:                   '',
    prizeTerms:              '',
    prizeImage:              defaultImageObject,
    open:                    false,
  },
  ...defaultCropperState,
};

const Stamps = ({ match }) => {
  const { pageId, loyaltyCardId } = match.params;

  const [loading, setLoading] = useState(false);
  const [stampCountInput, setStampCountInput] = useState(null);
  const [stampTextColourPickerOpen, setStampTextColourPickerOpen] = useState(false);
  const [stampTextColourInput, setStampTextColourInput] = useState(defaultLoyaltyStampState.stampTextColour);
  const [loyaltyStamps, setLoyaltyStamps] = useReducer((state, action) => (
    { ...state, ...action, dialog: { ...state.dialog, ...action.dialog } }
  ), defaultLoyaltyStampState);
  const [isReduceStampDialogOpen, setIsReduceStampDialogOpen] = useState(false);

  const fileInputRefs = {
    defaultStampImage: useRef(null),
    stampsHolderImage: useRef(null),
    redeemImage:       useRef(null),
    stampImage:        useRef(null),
    prizeImage:        useRef(null),
  };

  const isDeactivated = loyaltyStamps.status === Status.Deactivated;
  const isDraft = loyaltyStamps.status === Status.Draft;

  const getLoyaltyStamps = async () => {
    try {
      setLoading(true);

      const { loyaltyCard, stamps } = await ApiService.getStampInfos(pageId, loyaltyCardId);

      const displayedStampsCount = loyaltyStamps.stampCount > stamps.length ? loyaltyStamps.stampCount : stamps.length;
      const stampTextColour = (!IsEmpty(loyaltyCard.stampTextColour) && isHexColour(`#${loyaltyCard.stampTextColour}`)) ? loyaltyCard.stampTextColour : loyaltyStamps.stampTextColour;

      setStampTextColourInput(stampTextColour);
      setStampCountInput(displayedStampsCount);
      setLoyaltyStamps({
        dialog:            defaultLoyaltyStampState.dialog,
        status:            loyaltyCard.status,
        defaultStampImage: { data: loyaltyCard.stampImage },
        stampsHolderImage: { data: loyaltyCard.stampsHolderImage },
        redeemImage:       { data: loyaltyCard.redeemStampImage },
        name:              loyaltyCard.name,
        terms:             loyaltyCard.terms,
        validityInDays:    loyaltyCard.validityInDays,
        stampTextColour,
        stamps,
        stampCount:        displayedStampsCount,
      });
      setStampCountInput(displayedStampsCount);
    } catch (err) {
      toast.error(err.message);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const validateStampCount = (stampCount) => {
    if (stampCount < 0) {
      toast.error('Number of stamps cannot be negative');
      return false;
    }

    if (stampCount > 12) {
      toast.error('Maximum number of 12 stamps exceeded. Please reduce number of stamps.');
      return false;
    }
    return true;
  };

  const handleSaveStampCount = async () => {
    try {
      setLoading(true);
      const stampCount = Number(stampCountInput);

      if (validateStampCount(stampCount)) {
        const stamps = await ApiService.updateLoyaltyCardStamps({ pageId, loyaltyCardId }, { stampCount });
        setLoyaltyStamps({ stamps, stampCount: stamps.length });
        toast.success("Loyalty card's stamps were updated");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fileValidated = (file) => {
    if (!IMAGE_FORMATS.includes(file.type)) {
      toast.error('Only JPG, JPEG and PNG files are supported.');

      return false;
    }

    if (file.size > BinaryOneMBSize * 7) {
      toast.error('Maximum file size of 7MB exceeded.');

      return false;
    }

    return true;
  };

  const handleSaveStampsHolderImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const imageUrl = await ApiService.uploadStampsHolderMedia({ pageId, loyaltyCardId }, formData, file);
      setLoyaltyStamps({ ...defaultCropperState, stampsHolderImage: { data: imageUrl } });
      toast.success('Background image has been updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveDefaultStampImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const imageUrl = await ApiService.uploadDefaultStampMedia({ pageId, loyaltyCardId }, formData, file);

      setLoyaltyStamps({ ...defaultCropperState, defaultStampImage: { data: imageUrl } });

      toast.success('Default stamp image has been updated');
    } catch (err) {
      toast.error(err.message);
      console.log(err);
    }
  };

  const handleSaveRedeemStamp = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const imageUrl = await ApiService.uploadRedeemStampMedia({ pageId, loyaltyCardId }, formData, file);

      setLoyaltyStamps({ ...defaultCropperState, redeemImage: { data: imageUrl } });

      toast.success('Redeemed stamp image has been updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveDialogStamp = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const { id: stampInfoId } = loyaltyStamps.dialog;

      const imageUrl = await ApiService.uploadStampInfoMedia({ pageId, loyaltyCardId, stampInfoId }, formData, file);
      const stamps = loyaltyStamps.stamps.map((stamp) => (stamp.id === stampInfoId ? { ...stamp, image: imageUrl } : stamp));
      setLoyaltyStamps({
        ...defaultCropperState,
        dialog: { stampImage: { data: imageUrl } },
        stamps,
      });
      toast.success(`Stamp's image has been updated`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSavePrizeImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const { id: stampInfoId } = loyaltyStamps.dialog;

      const imageUrl = await ApiService.uploadStampPrizeMedia({ pageId, loyaltyCardId, stampInfoId }, formData, file);
      const stamps = loyaltyStamps.stamps.map((stamp) => (stamp.id === stampInfoId ? { ...stamp, prizeImage: imageUrl } : stamp));
      setLoyaltyStamps({
        ...defaultCropperState,
        dialog: { prizeImage: { data: imageUrl } },
        stamps,
      });
      toast.success("Stamp's prize image has been updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = async (file, uploadContext) => {
    try {
      setLoading(true);
      if (IsEmpty(file)) {
        setLoyaltyStamps({ ...defaultCropperState });
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        switch (uploadContext || loyaltyStamps.uploadContext) {
          case 'stampsHolderImage':
            await handleSaveStampsHolderImage(file);
            break;
          case 'defaultStampImage':
            await handleSaveDefaultStampImage(file);
            break;
          case 'redeemImage':
            await handleSaveRedeemStamp(file);
            break;
          case 'stampImage':
            await handleSaveDialogStamp(file);
            break;
          case 'prizeImage':
            await handleSavePrizeImage(file);
            break;
          default:
            break;
        }
        setLoading(false);
      };
      return true;
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
  };

  const handleSaveStampTextColour = async () => {
    try {
      setLoading(true);

      if (stampTextColourInput !== loyaltyStamps.stampTextColour) {
        const { name, terms, validityInDays, status } = loyaltyStamps;
        const loyaltyCard = await ApiService.updateLoyaltyCard(
          pageId,
          loyaltyCardId,
          {
            name,
            terms,
            validityInDays,
            status,
            stampTextColour: isHexColour(`#${stampTextColourInput}`) ? stampTextColourInput : Colours.Black.slice(1),
          },
        );
        setLoyaltyStamps({
          name:            loyaltyCard.name,
          terms:           loyaltyCard.terms,
          validityInDays:  loyaltyCard.validityInDays,
          status:          loyaltyCard.status,
          stampTextColour: loyaltyCard.stampTextColour,
        });
        setStampTextColourInput(loyaltyCard.stampTextColour);
        toast.success('Stamp text colour has been updated', { toastId: `stamp-text-colour-${stampTextColourInput}` });
        setStampTextColourPickerOpen(false);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const fileInput = e.target;

    if (fileInput.files.length === 0) {
      return;
    }

    const file = fileInput.files[0];

    if (!fileValidated(file)) {
      return;
    }

    if (!['stampsHolderImage'].includes(fileInput.name)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageObj = { file, data: reader.result };
        const cropperTriggered = { isCropping: true, uploadContext: fileInput.name, cropImage: imageObj };
        setLoyaltyStamps({ ...cropperTriggered });

        // clear file input
        fileInputRefs[fileInput.name].current.value = '';
      };
      reader.readAsDataURL(file);
    } else {
      await handleSave(file, fileInput.name);
      fileInputRefs[fileInput.name].current.value = '';
    }
  };

  const openCustomizeDialog = (stamp, num) => {
    const stampInfo = stamp ? {
      id:                      stamp.id,
      stampImage:              { data: stamp.image || null },
      num,
      name:                    stamp.name,
      label:                   stamp.label,
      linkPageId:              stamp.linkPageId,
      linkPageName:            stamp.linkPageName,
      isToBeIssueByLinkPageId: stamp.isToBeIssueByLinkPageId,
      prize:                   stamp.prize,
      prizeTerms:              stamp.prizeTerms,
      prizeImage:              { data: stamp.prizeImage || null },
    } : {};

    setLoyaltyStamps({
      dialog: { ...stampInfo, open: true },
    });
  };

  const onCloseCustomizeDialog = async () => {
    setLoyaltyStamps({ dialog: { open: false } });
  };

  const onCloseCustomizeDialogEnd = () => {
    const { dialog } = loyaltyStamps;

    if (!dialog.open) {
      setLoyaltyStamps({ dialog: defaultLoyaltyStampState.dialog });
    }
  };

  const handleCustomizeInput = (e) => {
    setLoyaltyStamps({
      dialog: { [e.target.name]: (e.target.type === 'checkbox' ? e.target.checked : e.target.value) },
    });
  };

  const handleDialogSave = async () => {
    try {
      setLoading(true);
      const { id, name, label, prize, prizeTerms, linkPageId, isToBeIssueByLinkPageId } = loyaltyStamps.dialog;

      const updatedStamp = await ApiService.updateStampInfo(
        { pageId, loyaltyCardId, stampInfoId: id },
        { name, label, prize, prizeTerms, linkPageId, isToBeIssueByLinkPageId },
      );
      setLoyaltyStamps({
        stamps: loyaltyStamps.stamps.map((stamp) => (stamp.id === updatedStamp.id ? updatedStamp : stamp)),
        dialog: { open: false },
      });

      getLoyaltyStamps();
      toast.success('Stamp updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeCropDialog = () => {
    setLoyaltyStamps({
      cropImage:     defaultImageObject,
      isCropping:    false,
      uploadContext: null,
    });
  };

  useEffect(() => {
    getLoyaltyStamps();
  }, []);

  return (
    <div className="container-fluid">
      {loading && <OverLoading />}
      <div className="row mb-3">
        <div className="col-12 col-xl-6 pl-0">
          <Typography
            style={{
              fontSize: 12,
              color:    Colours.DarkGray,
            }}
            className="mb-2"
          >
            Loyalty Card ID:
            {' '}
            {match.params.loyaltyCardId}
          </Typography>
          <Typography style={{ fontWeight: 'bold', fontSize: 16 }}>
            Stamp Settings
          </Typography>
        </div>
      </div>

      <div className="row">
        <div className="pl-0 col-12 col-lg-8 col-xl-7 d-flex flex-column flex-lg-row">
          <div className="mb-2 mb-lg-0 mr-2">
            <Typography style={{ fontSize: 13 }}>Set the number of stamps per card</Typography>
            <Typography style={{ fontSize: 13 }}>(Max 12)</Typography>
          </div>

          <div className="d-flex align-items-center ml-0 ml-lg-4" style={{ width: 250 }}>
            <TextField
              size="small"
              variant="outlined"
              className="w-50"
              name="stampCount"
              type="number"
              disabled={!isDraft}
              value={stampCountInput || 0}
              onChange={(e) => setStampCountInput((e.target.value && Number(e.target.value) >= 0) ? String(Number(e.target.value)) : 0)}
              inputProps={{
                style:      { fontSize: 13 },
                inputProps: {
                  style: { textAlign: 'center' },
                },
              }}
            />
            <Button
              onClick={() => {
                if (validateStampCount(stampCountInput)) {
                  if (Number(stampCountInput) < loyaltyStamps.stampCount) {
                    setIsReduceStampDialogOpen(true);
                  } else {
                    handleSaveStampCount();
                  }
                }
              }}
              variant="contained"
              color="secondary"
              className="w-50 ml-2"
              disabled={!isDraft}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="pl-0 col-11 col-lg-8 col-xl-7 my-4">
          <Divider />
        </div>
      </div>

      <div className="row">
        <div className="pl-0 col-12 col-md-8 col-lg-6">
          <div className="container-fluid">
            <div className="row">
              <Typography className="font-weight-bold" style={{ fontSize: 13 }}>Details</Typography>
            </div>
            <div className="row mb-4">
              <div className="col-6 col-lg-5 pl-0">
                <Typography style={{ fontSize: 13 }} className="mb-2">Default stamp image</Typography>
                <div className="d-flex">
                  {loyaltyStamps.defaultStampImage.data
                    ? (
                      <img
                        src={loyaltyStamps.defaultStampImage.data}
                        alt="Stamp Placeholder"
                        style={{ width: 60, height: 60 }}
                        className="rounded-circle"
                      />
                    )
                    : <div className="rounded-circle" style={{ backgroundColor: Colours.Gray4, width: 60, height: 60 }} />}
                  <input
                    hidden
                    name="defaultStampImage"
                    accept={IMAGE_FORMATS}
                    type="file"
                    onChange={handleImageChange}
                    ref={fileInputRefs.defaultStampImage}
                  />
                  <div className="pl-1 d-flex justify-content-center align-items-center">
                    {!isDeactivated && (
                      <IconButton onClick={() => fileInputRefs.defaultStampImage.current.click()}>
                        <EditIcon htmlColor={Colours.Black} />
                      </IconButton>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-6 col-lg-5 pl-0">
                <Typography style={{ fontSize: 13 }} className="mb-2">Redeemed stamp image</Typography>
                <div className="d-flex">
                  {!IsEmpty(loyaltyStamps.redeemImage.data) ? (
                    <img
                      src={loyaltyStamps.redeemImage.data}
                      alt="Stamp Redeem"
                      style={{ width: 60, height: 60 }}
                      className="rounded-circle"
                    />
                  )
                    : <div className="rounded-circle" style={{ backgroundColor: Colours.Gray4, width: 60, height: 60 }} />}
                  <input
                    hidden
                    name="redeemImage"
                    accept={IMAGE_FORMATS}
                    type="file"
                    onChange={handleImageChange}
                    ref={fileInputRefs.redeemImage}
                  />
                  <div className="pl-1 d-flex justify-content-center align-items-center">
                    {!isDeactivated && (
                      <IconButton onClick={() => fileInputRefs.redeemImage.current.click()}>
                        <EditIcon htmlColor={Colours.Black} />
                      </IconButton>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 pl-0">
                <Typography className="mb-2" style={{ fontSize: 13 }}>Stamp&apos;s label colour</Typography>
              </div>
              <div className="col-12 pl-0 d-flex align-items-center">
                <Typography className="mr-1" style={{ fontSize: 13 }}>#</Typography>
                <TextField
                  disabled
                  variant="outlined"
                  size="small"
                  className="mr-2"
                  style={{ width: 100 }}
                  value={stampTextColourInput}
                  inputProps={{ style: { fontSize: 13, color: Colours.Black } }}
                />
                <FloatingColourPicker
                  open={stampTextColourPickerOpen}
                  placement="bottom"
                  colour={`#${stampTextColourInput}`}
                  onChange={(value) => setStampTextColourInput(value.slice(1))}
                  onClose={async (e) => {
                    e.stopPropagation();
                    setStampTextColourPickerOpen(false);
                    await handleSaveStampTextColour();
                  }}
                >
                  <Button
                    disabled={isDeactivated}
                    onClick={(e) => {
                      e.stopPropagation();
                      setStampTextColourPickerOpen((prevState) => !prevState);
                    }}
                    style={{
                      height:          '100%',
                      width:           100,
                      border:          `1px solid ${Colours.Gray3}`,
                      backgroundColor: `#${stampTextColourInput}`,
                      borderRadius:    0,
                      cursor:          'pointer',
                    }}
                  />
                </FloatingColourPicker>
              </div>
            </div>

            {!IsEmpty(loyaltyStamps.stamps) && (
              <div className="row mb-3">
                <div className="col-12 pl-0">
                  <Typography className="mb-2" style={{ fontSize: 13 }}>Customise individual stamp and card background</Typography>
                </div>
                <div className="col-12 pl-0">
                  <div
                    className="position-relative pt-5 px-2"
                    style={{
                      borderRadius:       20,
                      width:              320,
                      backgroundColor:    IsEmpty(loyaltyStamps.stampsHolderImage.data) ? Colours.LightGray1 : null,
                      backgroundImage:    IsEmpty(loyaltyStamps.stampsHolderImage.data) ? null : `url(${loyaltyStamps.stampsHolderImage.data})`,
                      backgroundSize:     'cover',
                      backgroundPosition: 'center top',
                    }}
                  >
                    <div className="container-fluid">
                      <div className="row">
                        {loyaltyStamps.stamps.map((stamp) => (
                          <div key={stamp.id} className="col-4 d-flex flex-column justify-content-start align-items-center mb-4">
                            <IconButton size="small" onClick={() => openCustomizeDialog(stamp, stamp.stampNum)} className="p-0 mb-1">
                              <div
                                className="rounded-circle d-flex justify-content-center align-items-center position-relative"
                                style={{ backgroundColor: Colours.Gray4, width: 55, height: 55 }}
                              >
                                {(stamp.image || loyaltyStamps.defaultStampImage.data) && (
                                  <img
                                    src={stamp.image || loyaltyStamps.defaultStampImage.data}
                                    alt="Stamp"
                                    className="w-100 h-100 rounded-circle position-absolute"
                                    style={{ filter: isDeactivated ? null : 'contrast(25%)' }}
                                  />
                                )}
                                {!isDeactivated && <EditIcon htmlColor={Colours.Black} className="position-absolute" />}
                              </div>
                            </IconButton>
                            <Typography style={{ fontSize: 11, color: `#${stampTextColourInput}` }} className="text-center font-weight-bold">{stamp.name}</Typography>
                            <Typography style={{ fontSize: 11, color: `#${stampTextColourInput}` }} className="text-center">{stamp.label}</Typography>
                            <div style={{
                              fontSize:   '10pt',
                              visibility: (stamp && stamp.isToBeIssueByLinkPageId) ? 'visible' : 'hidden',
                              color:      `#${stampTextColourInput}`,
                            }}
                            >
                              <LockIcon fontSize="inherit" color="inherit" />
                            </div>
                          </div>
                        ))}
                      </div>

                      {!isDeactivated && (
                        <div className="row">
                          <div className="col px-0 d-flex align-items-center justify-content-center">
                            <input
                              hidden
                              name="stampsHolderImage"
                              accept={IMAGE_FORMATS}
                              type="file"
                              onChange={handleImageChange}
                              ref={fileInputRefs.stampsHolderImage}
                            />
                            <Button onClick={() => fileInputRefs.stampsHolderImage.current.click()} className="mb-3">
                              {/* filter for background image */}
                              {loyaltyStamps.stampsHolderImage.data && (
                                <div
                                  className="w-100 h-100 position-absolute"
                                  style={{
                                    top:            0,
                                    left:           0,
                                    opacity:        '70%',
                                    backdropFilter: 'blur(2px) contrast(40%) brightness(140%)',
                                    overflow:       'hidden',
                                    borderRadius:   5,
                                  }}
                                />
                              )}
                              <div className="w-100 d-flex justify-content-center align-items-center" style={{ zIndex: 1 }}>
                                <Typography style={{ fontSize: 11, textTransform: 'initial' }} className="mr-2">Set background image</Typography>
                                <EditIcon fontSize="small" />
                              </div>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!IsEmpty(loyaltyStamps.stamps) && (
          <div className="col-12 col-xl-6 pr-xl-2 d-none d-xl-flex flex-column align-items-end">
            <div className="mr-xl-3" style={{ width: 410 }}>
              <div className="mb-2 pl-3 font-weight-bold">
                <Typography className="font-weight-bold" style={{ fontSize: 13 }}>Preview</Typography>
              </div>
              <div
                className="pt-5 px-4"
                style={{
                  borderRadius:       20,
                  backgroundColor:    IsEmpty(loyaltyStamps.stampsHolderImage.data) ? Colours.LightGray1 : null,
                  backgroundImage:    IsEmpty(loyaltyStamps.stampsHolderImage.data) ? null : `url(${loyaltyStamps.stampsHolderImage.data})`,
                  backgroundSize:     'cover',
                  backgroundPosition: 'center top',
                }}
              >
                <div className="container-fluid">
                  <div className="row">
                    {loyaltyStamps.stamps.map((stamp) => (
                      <div key={stamp.id} className="col-4 d-flex flex-column justify-content-start align-items-center pb-5">
                        <div className="rounded-circle mb-1" style={{ backgroundColor: Colours.Gray4, width: 60, height: 60 }}>
                          {(stamp.image || loyaltyStamps.defaultStampImage.data)
                          && <img src={stamp.image || loyaltyStamps.defaultStampImage.data} alt="stamp" className="w-100 h-100 rounded-circle" />}
                        </div>
                        <Typography className="font-weight-bold text-center" style={{ fontSize: 12, color: `#${stampTextColourInput}` }}>{stamp.name}</Typography>
                        <Typography className="font-weight-bold text-center" style={{ fontSize: 12, color: `#${stampTextColourInput}` }}>{stamp.label}</Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <StampDialog
        onCloseDialog={onCloseCustomizeDialog}
        onCloseDialogEnd={onCloseCustomizeDialogEnd}
        loyaltyStamps={loyaltyStamps}
        onUpdateLoyaltyStamps={setLoyaltyStamps}
        handleCustomizeInput={handleCustomizeInput}
        handleDialogSave={handleDialogSave}
        handleImageChange={handleImageChange}
        fileInputRefs={fileInputRefs}
        IMAGE_FORMATS={IMAGE_FORMATS}
        isDeactivated={isDeactivated}
      />

      <Dialog
        open={isReduceStampDialogOpen}
        onClose={() => setIsReduceStampDialogOpen(false)}
        PaperProps={{ className: 'p-5 w-100' }}
      >
        <Typography className="mb-4 font-weight-bold" style={{ fontSize: 20 }}>Reduce stamp confirmation</Typography>
        <Typography style={{ fontSize: 15 }} className="mb-2">You have selected to reduce the number of stamps from:</Typography>
        <div className="d-flex align-items-center" style={{ marginBottom: '2.2rem' }}>
          <Typography style={{ fontSize: 24 }} className="ml-2 font-weight-bold">{String(loyaltyStamps.stampCount)}</Typography>
          <Typography style={{ fontSize: 15 }} className="ml-4">to</Typography>
          <Typography style={{ fontSize: 24 }} className="ml-4 font-weight-bold">{String(stampCountInput)}</Typography>
        </div>
        <Typography style={{ fontSize: 15 }} className="mb-4">
          {`The last ${loyaltyStamps.stampCount - Number(stampCountInput) === 1
            ? `stamp's` : `${loyaltyStamps.stampCount - Number(stampCountInput)} stamps'`
          } images and details will be deleted:`}
        </Typography>
        <div className="container-fluid mb-4 px-0">
          <div className="row">
            {loyaltyStamps.stamps.slice(Number(stampCountInput)).map((stamp) => (
              <div key={stamp.id} className="col-4 col-sm-3 col-md-2 d-flex flex-column justify-content-start pb-4">
                <div
                  className="rounded-circle mb-1"
                  style={{ backgroundColor: Colours.Gray4, width: 60, height: 60 }}
                >
                  {(stamp.image || loyaltyStamps.defaultStampImage.data)
                && <img src={stamp.image || loyaltyStamps.defaultStampImage.data} alt="stamp" className="w-100 rounded-circle" />}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Typography style={{ fontSize: 15, marginBottom: '0.75rem' }} className="font-italic font-weight-bold">
          *Once a stamp is deleted, its information cannot be recovered.
        </Typography>
        <Typography className="font-weight-bold mb-5" style={{ fontSize: 15 }}>
          Proceed?
        </Typography>
        <div className="d-flex align-items-center justify-content-around mx-auto" style={{ width: '80%' }}>
          <Button variant="contained" onClick={() => setIsReduceStampDialogOpen(false)} style={{ width: '40%', backgroundColor: Colours.Gray2 }}>
            <Typography style={{ fontSize: 13 }} color={Colours.White}>
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="secondary"
            style={{ width: '40%' }}
            onClick={() => {
              handleSaveStampCount();
              setIsReduceStampDialogOpen(false);
            }}
          >
            <Typography style={{ fontSize: 13 }} color={Colours.White}>
              Reduce
            </Typography>
          </Button>
        </div>
      </Dialog>

      <Dialog fullScreen open={loyaltyStamps.isCropping} onClose={closeCropDialog}>
        <FixedCropper
          width={4}
          height={3}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={900}
          outputPixelHeight={900}
          type="upload"
          label={!['prizeImage'].includes(loyaltyStamps.uploadContext) && 'Logo'}
          src={loyaltyStamps.cropImage.data}
          file={loyaltyStamps.cropImage.file}
          handleClose={closeCropDialog}
          handleSave={handleSave}
          uploading={loading}
        />
      </Dialog>
    </div>
  );
};

Stamps.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id:            PropTypes.string,
      loyaltyCardId: PropTypes.string,
      pageId:        PropTypes.string,
    }),
  }).isRequired,
};

export default Stamps;
